import { useEffect, useState } from "react";
import { Check, CheckCircle2, Loader2, Palette } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { StepTitle } from "./AboutSteps";
import { LivePreview } from "../LivePreview";
import { PlanRecommendationCard } from "../PlanRecommendationCard";
import { DesignPicker } from "./DesignPicker";
import type { PreviewSource } from "@/lib/onboarding/buildPreviewEvent";
import type { OnboardingAnswers, PlanChoice } from "@/lib/onboarding/types";

/* ------------------------------------------------------------------ W07 */

interface RsvpDeadlineStepProps {
  answers: OnboardingAnswers;
  suggested: boolean;
  error: string | null;
  onChange: (date: string | null) => void;
}

export function RsvpDeadlineStep({ answers, suggested, error, onChange }: RsvpDeadlineStepProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <StepTitle>{t("onboarding.rsvpDeadline.title")}</StepTitle>
      {answers.date ? (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="onboarding-rsvp-deadline" className="text-base">
              {t("onboarding.rsvpDeadline.label")}
            </Label>
            <Input
              id="onboarding-rsvp-deadline"
              type="date"
              value={answers.rsvpDeadline ?? ""}
              max={answers.date}
              onChange={(e) => onChange(e.target.value || null)}
              className="h-12 text-lg"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? "onboarding-rsvp-deadline-error" : undefined}
              data-testid="onboarding-rsvp-deadline"
              autoFocus
            />
            {error && (
              <p id="onboarding-rsvp-deadline-error" className="text-sm text-destructive">
                {t(`onboarding.rsvpDeadline.errors.${error}`)}
              </p>
            )}
          </div>
          {suggested && <p className="text-sm text-muted-foreground">{t("onboarding.rsvpDeadline.suggested")}</p>}
          <p className="text-sm text-muted-foreground">{t("onboarding.rsvpDeadline.hint")}</p>
        </>
      ) : (
        <p className="text-muted-foreground" data-testid="onboarding-rsvp-no-date">
          {t("onboarding.rsvpDeadline.noDate")}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ W08 */

export function RsvpQuestionsStep() {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <StepTitle>{t("onboarding.rsvpQuestions.title")}</StepTitle>
      <ul className="space-y-3">
        {(["attending", "partySize"] as const).map((key) => (
          <li key={key} className="flex items-center gap-3 rounded-2xl border-2 border-pink-500 bg-pink-50 p-4 dark:bg-pink-950/30">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-pink-500 text-white" aria-hidden="true">
              <Check className="h-4 w-4" />
            </span>
            <span className="font-medium">{t(`onboarding.rsvpQuestions.${key}`)}</span>
          </li>
        ))}
      </ul>
      <p className="text-sm text-muted-foreground">{t("onboarding.rsvpQuestions.more")}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ W09 */

export function GeneratingStep({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const tick = reduced ? 150 : 550;
    const timers = [1, 2, 3].map((n) => setTimeout(() => setShown(n), n * tick));
    const done = setTimeout(onDone, 4 * tick);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(done);
    };
  }, [onDone]);

  return (
    <div className="space-y-6 pt-8 text-center" data-testid="onboarding-generating">
      <StepTitle>{t("onboarding.generating.title")}</StepTitle>
      <ul className="mx-auto max-w-xs space-y-3 text-left" aria-live="polite">
        {(["schedule", "rsvp", "page"] as const).map((key, index) => (
          <li key={key} className="flex items-center gap-3">
            {shown > index ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden="true" />
            ) : (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
            )}
            <span className={shown > index ? "" : "text-muted-foreground"}>{t(`onboarding.generating.${key}`)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ W10 */

interface PreviewStepProps {
  source: PreviewSource;
  title: string;
  busy: boolean;
  pendingPlan: PlanChoice | null;
  onContinue: () => void;
  onGuestView: () => void;
  onDesign: () => void;
  /** JUB-213: switch the design right here; the phone mock above follows at once. */
  onSelectDesign: (key: string) => void;
  onLater: () => void;
  onEditAnswers: () => void;
  onCheckout: (plan: PlanChoice) => void;
  onPackageAnswers: (answers: NonNullable<OnboardingAnswers["packageAnswers"]>) => void;
}

export function PreviewStep(props: PreviewStepProps) {
  const { t } = useTranslation();
  const { source, title, busy } = props;
  return (
    <div className="space-y-6" data-testid="onboarding-preview">
      <div className="text-center">
        <p className="mb-1 text-sm font-medium text-pink-600">{t("onboarding.preview.subtitle")}</p>
        <StepTitle>{title}</StepTitle>
      </div>

      <LivePreview source={source} heightClassName="h-[560px]" />
      <p className="text-center text-sm text-muted-foreground">{t("onboarding.preview.draftNote")}</p>

      <div className="space-y-3">
        <Button
          type="button"
          size="lg"
          className="h-12 w-full bg-gradient-to-r from-pink-500 to-rose-500 text-base text-white hover:from-pink-600 hover:to-rose-600"
          onClick={props.onContinue}
          disabled={busy}
          data-testid="onboarding-preview-continue"
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {t("onboarding.preview.continue")}
        </Button>
        <Button
          type="button"
          size="lg"
          variant="outline"
          className="h-12 w-full text-base"
          onClick={props.onGuestView}
          disabled={busy}
          data-testid="onboarding-preview-guest"
        >
          {t("onboarding.preview.guestView")}
        </Button>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button type="button" variant="link" className="h-auto p-0" onClick={props.onEditAnswers} data-testid="onboarding-preview-edit">
            {t("onboarding.preview.editAnswers")}
          </Button>
          <Button type="button" variant="link" className="h-auto p-0 text-muted-foreground" onClick={props.onLater} data-testid="onboarding-preview-later">
            {t("onboarding.preview.later")}
          </Button>
        </div>
      </div>

      {/* JUB-213: the designs to choose from (built-in + active installed packages);
          JUB-211: the editor link reads as a full-width button on phones. */}
      <div className="space-y-3 rounded-2xl border bg-background p-4" data-testid="onboarding-preview-design-card">
        <div className="flex items-center gap-3">
          <Palette className="h-5 w-5 shrink-0 text-pink-500" aria-hidden="true" />
          <p id="onboarding-design-title" className="font-medium">
            {t("onboarding.preview.designTitle")}
          </p>
        </div>
        <DesignPicker
          catalog={source.catalog ?? null}
          value={source.theme}
          disabled={busy}
          labelledBy="onboarding-design-title"
          onSelect={props.onSelectDesign}
        />
        <Button type="button" variant="outline" size="sm" className="w-full sm:w-auto" onClick={props.onDesign} disabled={busy} data-testid="onboarding-preview-design">
          {t("onboarding.preview.designLink")}
        </Button>
      </div>

      <PlanRecommendationCard
        answers={source.answers.packageAnswers}
        pendingPlan={props.pendingPlan}
        busy={busy}
        onAnswers={props.onPackageAnswers}
        onCheckout={props.onCheckout}
      />
    </div>
  );
}
